import { Autocomplete, Chip, TextField, Typography } from '@mui/material';
import React, { useMemo } from 'react';

import AdaptiveChipList from 'ui/AdaptiveChipList/AdaptiveChipList';
import Flex from 'ui/Flex/Flex';
import { UserT } from 'business/definitions';
import { getUserLabel } from 'helpers/users';
import { useFetchUsers } from 'routes/Profile/hooks/fetchUsers';
import { useTranslation } from 'react-i18next';

type UserGroupMembersProps = {
    emails: string[];
    setEmails: (arg: string[]) => void;
};

const GroupMembersChips = (
    users: UserT[],
    onChipDelete: (user: UserT) => void,
) =>
    users.map((user) => (
        <Chip
            key={`group-member-${user.email}`}
            label={getUserLabel(user)}
            size="small"
            sx={{ width: 'fit-content' }}
            color="primary"
            onDelete={() => onChipDelete(user)}
        />
    ));

const UserGroupMembers = ({
    emails,
    setEmails,
}: UserGroupMembersProps): JSX.Element => {
    const { t } = useTranslation();

    const { data: users = [] } = useFetchUsers();
    const sortedUsers = useMemo(() => {
        const collator = new Intl.Collator('fr', { sensitivity: 'base' });
        return [...users].sort((a, b) =>
            collator.compare(getUserLabel(a), getUserLabel(b)),
        );
    }, [users]);

    const [usersThatAreNotGroupMembers, usersThatAreGroupMembers] =
        useMemo(() => {
            const notGroupMembers: UserT[] = [];
            const groupMembers: UserT[] = [];

            sortedUsers.forEach((user) => {
                if (emails.includes(user.email)) {
                    groupMembers.push(user);
                } else {
                    notGroupMembers.push(user);
                }
            });

            return [notGroupMembers, groupMembers];
        }, [sortedUsers, emails]);

    const onChipDelete = (user: UserT) => {
        const remainingEmails = emails.filter((email) => user.email !== email);
        setEmails(remainingEmails);
    };

    return (
        <Flex direction="column" sx={{ gap: 2, p: 1 }}>
            <Typography variant="body1SemiBold">
                {t('adminSpace.createUserGroup.usersSection')}
            </Typography>
            {emails.length > 0 && (
                <AdaptiveChipList
                    defaultMaxHeight="88px" // It represents the height of 3 chips + a gap of 8px between them
                    chipsListLength={emails.length}
                >
                    {GroupMembersChips(usersThatAreGroupMembers, onChipDelete)}
                </AdaptiveChipList>
            )}
            <Autocomplete
                disableClearable
                disableCloseOnSelect
                multiple
                options={usersThatAreNotGroupMembers}
                noOptionsText={t('common.noResult')}
                getOptionKey={(option) => option.email}
                getOptionLabel={getUserLabel}
                isOptionEqualToValue={(option: UserT, value: UserT) =>
                    option.email === value.email
                }
                onChange={(_, value) => {
                    setEmails(value.map((user) => user.email));
                }}
                value={usersThatAreGroupMembers}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t(
                            'adminSpace.createUserGroup.usersButtonPlaceholder',
                        )}
                    />
                )}
                renderTags={() => <></>}
                sx={{ width: '300px' }}
            />
        </Flex>
    );
};

export default UserGroupMembers;
